/**
 * analyticsExport.js
 * Client-side CSV export for Booking Analytics.
 * Works entirely in the browser from the existing API response — no backend endpoint needed.
 */

// ── Helpers ───────────────────────────────────────────────────────────────────

const pct  = (v) => v != null ? `${Number(v).toFixed(2)}%` : '—';
const num  = (v) => v != null ? String(Number(v))          : '—';
const str  = (v) => v != null ? String(v)                   : '—';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return iso; }
};

/** Escape a single CSV cell value (wrap in quotes if it contains commas/newlines/quotes). */
const cell = (v) => {
  const s = v != null ? String(v) : '';
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Build a row string from an array of values. */
const row = (...values) => values.map(cell).join(',');

/** Blank separator line */
const br = () => '';

/** Section header row (styled as a prominent label) */
const section = (title) => `\r\n>>> ${title.toUpperCase()}`;

// ── Section builders ──────────────────────────────────────────────────────────

function buildMeta(meta, label) {
  return [
    row('LONDON DOCTORS CLINIC — BOOKING ANALYTICS REPORT'),
    br(),
    row('Report Label',   label || 'Custom Range'),
    row('Date From',      fmtDate(meta.date_from)),
    row('Date To',        fmtDate(meta.date_to)),
    row('Generated At',   meta.computed_at ? new Date(meta.computed_at).toLocaleString('en-GB') : '—'),
    row('Data Cached',    meta.cached ? 'Yes' : 'No'),
    row('Total Sessions (All Journeys)', num(meta.total_sessions_all_journeys)),
  ];
}

function buildSummary(booking, cancelling, rescheduling) {
  return [
    section('Journey Summary'),
    row('Journey', 'Total Sessions', 'Confirmed / Succeeded', 'Expired', 'Abandoned', 'Pending', 'Failed', 'Conversion Rate %', 'Expired Rate %', 'Abandoned Rate %'),
    row('Booking',       num(booking.total),      num(booking.confirmed),   num(booking.expired),   num(booking.abandoned),   num(booking.pending),   num(booking.failed),   pct(booking.cr),      pct(booking.pct_expired),      pct(booking.pct_abandoned)),
    row('Cancellation',  num(cancelling.total),   num(cancelling.succeeded),num(cancelling.expired),num(cancelling.abandoned),num(cancelling.pending),num(cancelling.failed),pct(cancelling.cr),   pct(cancelling.pct_expired),   pct(cancelling.pct_abandoned)),
    row('Rescheduling',  num(rescheduling.total), num(rescheduling.succeeded),num(rescheduling.expired),num(rescheduling.abandoned),num(rescheduling.pending),num(rescheduling.failed),pct(rescheduling.cr),pct(rescheduling.pct_expired),pct(rescheduling.pct_abandoned)),
  ];
}

function buildByRoute(journey, label) {
  if (!journey.by_route || !journey.by_route.length) return [];
  return [
    section(`${label} — Booking Route Breakdown`),
    row('Route', 'Total Sessions', 'Confirmed/Booked', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_route.map(r => row(str(r.name), num(r.total), num(r.booked ?? r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByProductCategory(journey, label) {
  if (!journey.by_product_category || !journey.by_product_category.length) return [];
  return [
    section(`${label} — By Appointment Category`),
    row('Category', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_product_category.map(r => row(str(r.name), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByProduct(journey, label) {
  if (!journey.by_product || !journey.by_product.length) return [];
  return [
    section(`${label} — By Product / Appointment Type`),
    row('Product', 'Product ID', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_product.map(r => row(str(r.name), str(r.product_id ?? 'N/A'), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByDuration(journey, label) {
  if (!journey.by_duration || !journey.by_duration.length) return [];
  return [
    section(`${label} — By Appointment Duration`),
    row('Duration', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_duration.map(r => row(str(r.label), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByGender(journey, label) {
  if (!journey.by_gender || !journey.by_gender.length) return [];
  return [
    section(`${label} — By Doctor Gender Preference`),
    row('Gender Preference', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_gender.map(r => row(str(r.label ?? r.gender), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByLanguage(journey, label) {
  if (!journey.by_language || !journey.by_language.length) return [];
  return [
    section(`${label} — By Language Preference`),
    row('Language', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_language.map(r => row(str(r.name), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByClinic(journey, label) {
  if (!journey.by_clinic || !journey.by_clinic.length) return [];
  return [
    section(`${label} — By Clinic Performance`),
    row('Clinic', 'Clinic ID', 'Total Sessions', 'Confirmed', 'Conversion Rate %', 'Avg Duration'),
    ...journey.by_clinic.map(r => row(str(r.clinic_name), str(r.clinic_id), num(r.total), num(r.confirmed), pct(r.cr), str(r.avg_duration ?? '—'))),
  ];
}

function buildByHourBucket(journey, label) {
  if (!journey.by_hour_bucket || !journey.by_hour_bucket.length) return [];
  return [
    section(`${label} — Sessions By Time of Day`),
    row('Time Bucket', 'Total Sessions', 'Confirmed', 'Conversion Rate %'),
    ...journey.by_hour_bucket.map(r => row(str(r.bucket), num(r.total), num(r.confirmed), pct(r.cr))),
  ];
}

function buildByDayOfWeek(journey, label) {
  if (!journey.by_day_of_week || !journey.by_day_of_week.length) return [];
  return [
    section(`${label} — Sessions By Day of Week`),
    row('Day', 'Total Sessions', 'Confirmed', 'Conversion Rate %'),
    ...journey.by_day_of_week.map(r => row(str(r.day), num(r.total), num(r.confirmed), pct(r.cr))),
  ];
}

function buildByDevice(journey, label) {
  if (!journey.by_device || !journey.by_device.length) return [];
  return [
    section(`${label} — By Device`),
    row('Device', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_device.map(r => row(str(r.name), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildByBrowser(journey, label) {
  if (!journey.by_browser || !journey.by_browser.length) return [];
  return [
    section(`${label} — By Browser`),
    row('Browser', 'Total Sessions', 'Confirmed', 'Conversion Rate %', '% of Journey Sessions'),
    ...journey.by_browser.map(r => row(str(r.name), num(r.total), num(r.confirmed), pct(r.cr), pct(r.pct))),
  ];
}

function buildFunnel(journey, label) {
  if (!journey.funnel || !journey.funnel.length) return [];
  return [
    section(`${label} — User Journey Funnel (Top Events)`),
    row('Event / Step', 'Session Count', '% of Journey Sessions'),
    ...journey.funnel.map(r => row(str(r.label), num(r.count), pct(r.pct))),
  ];
}

function buildWeekly(journey, label) {
  if (!journey.weekly || !journey.weekly.length) return [];
  const isBooking = label === 'Booking';
  return [
    section(`${label} — Weekly Trend`),
    row(
      'Week', 'Total Sessions', 'Confirmed / Booked', 'Conversion Rate %',
      'Expired', 'Abandoned', 'Failed',
      ...(isBooking ? ['Clinic Booked', 'Clinic CR%', 'Time Booked', 'Time CR%'] : [])
    ),
    ...journey.weekly.map(r => row(
      str(r.week),
      num(r.total),
      num(r.booked ?? r.succeeded ?? r.confirmed),
      pct(r.cr),
      num(r.expired   ?? null),
      num(r.abandoned ?? null),
      num(r.failed    ?? null),
      ...(isBooking ? [
        num(r.clinic_booked ?? null),
        pct(r.clinic_cr     ?? null),
        num(r.time_booked   ?? null),
        pct(r.time_cr       ?? null),
      ] : [])
    )),
  ];
}

function buildDaily(journey, label) {
  if (!journey.daily || !journey.daily.length) return [];
  const isBooking = label === 'Booking';
  return [
    section(`${label} — Daily Trend`),
    row('Date', 'Total Sessions', isBooking ? 'Booked' : 'Succeeded', 'Conversion Rate %', 'Expired', 'Abandoned', 'Failed'),
    ...journey.daily.map(r => row(
      str(r.date),
      num(r.total),
      num(isBooking ? (r.booked ?? r.succeeded) : (r.succeeded ?? r.booked)),
      pct(r.cr),
      num(r.expired   ?? null),
      num(r.abandoned ?? null),
      num(r.failed    ?? null),
    )),
  ];
}

// ── Main export function ──────────────────────────────────────────────────────

/**
 * Generate and trigger a CSV download from an analytics API response.
 * @param {object} analyticsData  The full API `data` object (meta + booking + cancelling + rescheduling)
 * @param {string} [reportLabel]  Human-readable label, e.g. "Last 30 Days"
 */
export const exportAnalyticsCSV = (analyticsData, reportLabel = 'Analytics Report') => {
  const { meta, booking, cancelling, rescheduling } = analyticsData;

  const lines = [
    // ── Cover ──────────────────────────────────────────────────────────────
    ...buildMeta(meta || {}, reportLabel),
    br(),

    // ── Overview ───────────────────────────────────────────────────────────
    ...buildSummary(booking || {}, cancelling || {}, rescheduling || {}),
    br(),

    // ── Booking ────────────────────────────────────────────────────────────
    ...buildByRoute(booking || {}, 'Booking'),
    ...buildByProductCategory(booking || {}, 'Booking'),
    ...buildByProduct(booking || {}, 'Booking'),
    ...buildByDuration(booking || {}, 'Booking'),
    ...buildByGender(booking || {}, 'Booking'),
    ...buildByLanguage(booking || {}, 'Booking'),
    ...buildByClinic(booking || {}, 'Booking'),
    ...buildByDevice(booking || {}, 'Booking'),
    ...buildByBrowser(booking || {}, 'Booking'),
    ...buildByHourBucket(booking || {}, 'Booking'),
    ...buildByDayOfWeek(booking || {}, 'Booking'),
    ...buildWeekly(booking || {}, 'Booking'),
    ...buildDaily(booking || {}, 'Booking'),
    ...buildFunnel(booking || {}, 'Booking'),
    br(),

    // ── Cancellation ───────────────────────────────────────────────────────
    ...buildByProductCategory(cancelling || {}, 'Cancellation'),
    ...buildByProduct(cancelling || {}, 'Cancellation'),
    ...buildByDuration(cancelling || {}, 'Cancellation'),
    ...buildByGender(cancelling || {}, 'Cancellation'),
    ...buildByLanguage(cancelling || {}, 'Cancellation'),
    ...buildByClinic(cancelling || {}, 'Cancellation'),
    ...buildByBrowser(cancelling || {}, 'Cancellation'),
    ...buildByHourBucket(cancelling || {}, 'Cancellation'),
    ...buildByDayOfWeek(cancelling || {}, 'Cancellation'),
    ...buildWeekly(cancelling || {}, 'Cancellation'),
    ...buildDaily(cancelling || {}, 'Cancellation'),
    br(),

    // ── Rescheduling ───────────────────────────────────────────────────────
    ...buildByProductCategory(rescheduling || {}, 'Rescheduling'),
    ...buildByProduct(rescheduling || {}, 'Rescheduling'),
    ...buildByDuration(rescheduling || {}, 'Rescheduling'),
    ...buildByGender(rescheduling || {}, 'Rescheduling'),
    ...buildByLanguage(rescheduling || {}, 'Rescheduling'),
    ...buildByClinic(rescheduling || {}, 'Rescheduling'),
    ...buildByBrowser(rescheduling || {}, 'Rescheduling'),
    ...buildByHourBucket(rescheduling || {}, 'Rescheduling'),
    ...buildByDayOfWeek(rescheduling || {}, 'Rescheduling'),
    ...buildWeekly(rescheduling || {}, 'Rescheduling'),
    ...buildDaily(rescheduling || {}, 'Rescheduling'),
    ...buildFunnel(rescheduling || {}, 'Rescheduling'),
    br(),
    row('— END OF REPORT —'),
  ];

  // Build CSV blob (UTF-8 BOM so Excel opens it correctly with accented chars)
  const bom     = '\uFEFF';
  const csv     = bom + lines.join('\r\n');
  const blob    = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url     = URL.createObjectURL(blob);
  const dateTag = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const a       = document.createElement('a');
  a.href        = url;
  a.download    = `ldc_analytics_${dateTag}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
